export type HubTab = 'marketplace' | 'workspace' | 'published';

export function resolveHubViewKey(
  activeTab: HubTab,
  params: URLSearchParams
): string {
  const detail = params.get('detail');
  if (detail) return `marketplace:detail:${detail}`;
  if (activeTab === 'workspace') {
    return `workspace:${params.get('workspaceView') === 'usages' ? 'contents' : 'templates'}`;
  }
  if (activeTab === 'published') return 'mine';
  const category = params.get('category');
  if (category) return `marketplace:category:${category}`;
  if (params.get('view') === 'all') return 'marketplace:all';
  return 'marketplace:discover';
}

const HUB_QUERY_PARAM = {
  detail: 'detail',
  detailReturnTab: 'detailReturnTab',
  detailReturnType: 'detailReturnType',
  detailAction: 'detailAction',
  tab: 'tab',
  type: 'type',
  update: 'update',
} as const;

/**
 * The Hub page that offers the update of an entry: the installations of the
 * entry in this workspace to choose from.
 */
export function openHubUpdateSearchParams(
  current: URLSearchParams,
  input: { templateId: string }
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set(HUB_QUERY_PARAM.tab, 'workspace');
  next.set('workspaceView', 'usages');
  next.set(HUB_QUERY_PARAM.update, input.templateId);
  return next;
}

/**
 * Where the notice of a new Hub version leads: the Hub page of the
 * installations of the entry in the workspace it was sent for.
 */
export function hubUpdateOfNotification(body: {
  workspaceId?: unknown;
  templateId?: unknown;
}): { workspaceId: string; params: URLSearchParams } | null {
  if (typeof body.workspaceId !== 'string' || !body.workspaceId) return null;
  if (typeof body.templateId !== 'string' || !body.templateId) return null;
  return {
    workspaceId: body.workspaceId,
    params: openHubUpdateSearchParams(new URLSearchParams(), {
      templateId: body.templateId,
    }),
  };
}

/** The update the Hub page offers, if it offers one. */
export function readHubUpdateSearchParams(
  params: URLSearchParams
): { templateId: string } | null {
  const templateId = params.get(HUB_QUERY_PARAM.update);
  return templateId ? { templateId } : null;
}

export function openHubDetailSearchParams(
  current: URLSearchParams,
  input: {
    detailId: string;
    templateType: string;
    returnTab?: Exclude<HubTab, 'marketplace'>;
    action?: 'add';
  }
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set(HUB_QUERY_PARAM.tab, 'marketplace');
  next.set(HUB_QUERY_PARAM.type, input.templateType);
  next.set(HUB_QUERY_PARAM.detail, input.detailId);
  if (input.action) next.set(HUB_QUERY_PARAM.detailAction, input.action);
  else next.delete(HUB_QUERY_PARAM.detailAction);
  if (input.returnTab) {
    next.set(HUB_QUERY_PARAM.detailReturnTab, input.returnTab);
    next.set(
      HUB_QUERY_PARAM.detailReturnType,
      current.get(HUB_QUERY_PARAM.detailReturnType) ??
        current.get(HUB_QUERY_PARAM.type) ??
        ''
    );
  } else {
    next.delete(HUB_QUERY_PARAM.detailReturnTab);
    next.delete(HUB_QUERY_PARAM.detailReturnType);
  }
  return next;
}

export function setHubDetailSearchParams(
  current: URLSearchParams,
  detailId: string
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set(HUB_QUERY_PARAM.detail, detailId);
  return next;
}

export function closeHubDetailSearchParams(
  current: URLSearchParams,
  options?: { destination?: 'return-tab' | 'marketplace' }
): URLSearchParams {
  const next = new URLSearchParams(current);
  const returnTab = next.get(HUB_QUERY_PARAM.detailReturnTab);
  const returnType = next.get(HUB_QUERY_PARAM.detailReturnType);
  next.delete(HUB_QUERY_PARAM.detail);
  next.delete(HUB_QUERY_PARAM.detailReturnTab);
  next.delete(HUB_QUERY_PARAM.detailReturnType);
  next.delete(HUB_QUERY_PARAM.detailAction);

  if (options?.destination === 'marketplace') {
    next.delete(HUB_QUERY_PARAM.tab);
  } else if (returnTab === 'published' || returnTab === 'workspace') {
    next.set(HUB_QUERY_PARAM.tab, returnTab);
    if (returnType) next.set(HUB_QUERY_PARAM.type, returnType);
    else if (returnType !== null) next.delete(HUB_QUERY_PARAM.type);
  }

  return next;
}

/** Entry pages of the three Hub areas: nothing inside the Hub to go back to. */
const HUB_AREA_ROOT_VIEW_KEYS = new Set([
  'marketplace:discover',
  'workspace:templates',
  'workspace:contents',
  'mine',
]);

export function isHubAreaRootViewKey(viewKey: string): boolean {
  return HUB_AREA_ROOT_VIEW_KEYS.has(viewKey);
}

/**
 * Structural parent of a Hub sub-page, used when Back has no Hub-internal
 * history entry (deep link or Hub opened fresh on a sub-page).
 */
export function hubParentSearchParams(
  current: URLSearchParams
): URLSearchParams {
  if (current.has(HUB_QUERY_PARAM.detail)) {
    return closeHubDetailSearchParams(current);
  }
  if (current.has('q')) {
    const next = new URLSearchParams(current);
    next.delete('q');
    next.delete('page');
    return next;
  }
  const next = new URLSearchParams();
  return next;
}

/** One search history entry: first input pushes, subsequent edits replace. */
export function hubSearchUpdate(current: URLSearchParams, value: string) {
  const next = new URLSearchParams(current);
  if (value) next.set('q', value);
  else next.delete('q');
  for (const key of [
    'page',
    'detail',
    'category',
    'type',
    'mineKind',
    'mineState',
  ]) {
    next.delete(key);
  }
  return {
    params: next,
    replace: Boolean(current.get('q')?.trim()) || !value.trim(),
  };
}
